import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PusherService } from '../_service/pusher.service';
import { WaiterService } from '../_service/waiter.service';
import { ChatNotificationService } from '../_service/chat-notification.service';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('messageSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX({{direction}})', scale: 0.8 }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)', scale: 1 }))
      ])
    ]),
    trigger('staggerMessages', [
      transition('* <=> *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          stagger('100ms', [
            animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class ChatComponent {
  newMessage = '';
  activeOrder: any;
  chatHistory: any[] = [];
  orderDetails: any;
  isLoading = false;
  isChatLoading = false;
  isOrderLoading = false;
  isSendingMessage = false;
  isTyping = false;

  constructor(
    private service: WaiterService,
    private route: ActivatedRoute,
    private pusherService: PusherService,
    private chatNotificationService: ChatNotificationService
  ) { }

  // Track by function for better performance
  trackByMessage(index: number, message: any): any {
    return message.id || index;
  }

  // Get message direction for animation
  getMessageDirection(senderType: string): string {
    return senderType === 'waiter' ? '100px' : '-100px';
  }

  // Format timestamp
  formatTimestamp(timestamp: string): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  ngOnInit(): void {
    this.isLoading = true;
    const orderId = this.route.snapshot.paramMap.get('id');
    if (orderId) {
      this.chatNotificationService.clearChatNotification(orderId);
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const pusher_id = user.user.pusher_id;
    const waiterChannel = this.pusherService.subscribeToChannel('waiter-notification');
    waiterChannel.bind(`waiter-notification-${pusher_id}`, (data: any) => {
      console.log(data);
      if (data.type === 'chat_details' && data.order_id === orderId) {
        this.chatHistory.push({
          body: data.data.body,
          sender_type: 'table',
          timestamp: new Date().toISOString()
        });
        setTimeout(() => this.scrollToBottom(), 100);
        this.playBeep();
      }
    });
    this.getOrderDetails();
    this.fetchChatHistory(orderId);
  }
  @ViewChild('chatBody', { static: true }) chatBody!: ElementRef;
  playBeep() {
    const audio = new Audio('livechat.mp3');
    audio.load();
    audio.play().catch(error => {
      console.error("Error playing audio", error);
    });
  }
  scrollToBottom() {
    this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight;
  }
  getOrderDetails() {
    this.isOrderLoading = true;
    this.service.viewOrder(this.route.snapshot.paramMap.get('id')).subscribe({
      next: (res) => {
        this.orderDetails = res;
      },
      error: (err) => {
        console.error('Error loading order details:', err);
      },
      complete: () => {
        this.isOrderLoading = false;
      }
    })
  }

  fetchChatHistory(orderId: any) {
    this.isChatLoading = true;
    this.service.fetchChatHistory(orderId).subscribe({
      next: (res: any) => {
        this.chatHistory = res.map((message: any) => ({
          ...message,
          timestamp: message.created_at || new Date().toISOString()
        }));
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (err: any) => {
        console.error('Error loading chat history:', err);
      },
      complete: () => {
        this.isChatLoading = false;
        this.isLoading = false;
      }
    });
  }

  sendChatMessage() {
    if (this.newMessage.trim() && !this.isSendingMessage) {
      this.isSendingMessage = true;
      const messageToSend = this.newMessage.trim();

      // Add message to chat immediately for better UX
      const tempMessage = {
        body: messageToSend,
        sender_type: 'waiter',
        timestamp: new Date().toISOString(),
        sending: true
      };
      this.chatHistory.push(tempMessage);
      setTimeout(() => this.scrollToBottom(), 100);

      this.newMessage = '';

      this.service.sendChatMessage(this.route.snapshot.paramMap.get('id'), messageToSend).subscribe({
        next: (res: any) => {
          // Update the temporary message to show it's sent
          const messageIndex = this.chatHistory.findIndex(msg => msg === tempMessage);
          if (messageIndex > -1) {
            this.chatHistory[messageIndex].sending = false;
            this.chatHistory[messageIndex].sent = true;
          }
        },
        error: (err: any) => {
          console.error('Error sending message:', err);
          // Remove the temporary message on error
          const messageIndex = this.chatHistory.findIndex(msg => msg === tempMessage);
          if (messageIndex > -1) {
            this.chatHistory.splice(messageIndex, 1);
          }
        },
        complete: () => {
          this.isSendingMessage = false;
        }
      });
    }
  }
}
